<template>
  <styled-payment-wrap>
    <template v-if="isStepperShown">
      <stepper v-if="!isMobile" />
      <mobile-status-bar v-else />
      <mobile-remaining-time-counter v-if="isMobile" />
    </template>
    <payment-header-component v-if="isHeaderShown" />
    <styled-payment-tab-select-component v-if="isFilterShown" />

    <router-view />

    <styled-spinner v-if="showGlobalLoader" />
  </styled-payment-wrap>
</template>

<script>
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';
import PaymentHeaderComponent from '@/components/Header/Header/PaymentHeaderComponent';
import Stepper from '@/components/Header/Stepper/Stepper';
import MobileStatusBar from '@/components/Header/Stepper/MobileStatusBar';
import MobileRemainingTimeCounter from '@/components/Header/Stepper/MobileRemainingTimeCounter';

import Spinner from '@/components/UI/Spinner';

import PaymentStatusController from '@/behaviors/paymentInvoiceController';
import PaymentTabSelectComponent from '@/components/Header/NavigationBar/PaymentTabSelectComponent';
import styled from 'vue-styled-components';

// API CLIENT JS LIBRARY
import MerchantSDK from '@paycore/merchant-sdk-js';
import postMessageService from '@/mixins/postMessageService';
import iframeDetection from '@/mixins/iframeDetection';

// For resirect if CPI passed
import Protocol from '@/behaviors/protocols/redirect';

const styledSpinner = styled(Spinner)`
  height: 100%;
  background-color: var(--primary_background_color);
`;

const styledPaymentWrap = styled.div`
  display: flex;
  flex-flow: column;
  height: 100%;
`;

const styledPaymentTabSelectComponent = styled(PaymentTabSelectComponent)`
  @media screen and (min-width: 769px) {
    .payment-methods__tabs-wrapper--mobile {
      display: none;
    }
  }
  @media screen and (max-width: 769px) {
    .payment-methods__tabs-wrapper--mobile {
      display: block;
    }
    .payment-methods__tabs-wrapper--desktop {
      display: none;
    }
  }
`;

/**
 * HARDCODE
 * Map of layout settings for invoice flow methods
 */
const layoutSettingsByMethodMap = {
  muchbetter: {
    hide_header: 'true',
    hide_progress_bar: 'true',
    hide_footer: 'true',
  },
  vodacom: {
    hide_header: 'true',
    hide_progress_bar: 'true',
  },
  airtel_money: {
    hide_header: 'true',
    hide_progress_bar: 'true',
  },
  tigo_pesa: {
    hide_header: 'true',
    hide_progress_bar: 'true',
  },
  halo_pesa: {
    hide_header: 'true',
    hide_progress_bar: 'true',
  },
  zantel_ezypesa: {
    hide_header: 'true',
    hide_progress_bar: 'true',
  },
  mts: {
    hide_header: 'true',
    hide_progress_bar: 'true',
  },
  megafon: {
    hide_header: 'true',
    hide_progress_bar: 'true',
  },
  beeline: {
    hide_header: 'true',
    hide_progress_bar: 'true',
  },
  tele2ru: {
    hide_header: 'true',
    hide_progress_bar: 'true',
  },
  uax: {
    hide_header: 'true',
    hide_footer: 'true',
    hide_progress_bar: 'true',
  },
  bitcoin: {
    hide_header: 'true',
    hide_footer: 'true',
    hide_progress_bar: 'true',
  },
  litecoin: {
    hide_header: 'true',
    hide_footer: 'true',
    hide_progress_bar: 'true',
  },
  bitcoin_cash: {
    hide_header: 'true',
    hide_footer: 'true',
    hide_progress_bar: 'true',
  },
  tether: {
    hide_header: 'true',
    hide_footer: 'true',
    hide_progress_bar: 'true',
  },
  paywise_cash: {
    hide_header: 'true',
    hide_progress_bar: 'true',
  },
  card_transfer: {
    hide_header: 'true',
    hide_footer: 'true',
    hide_progress_bar: 'true',
  },
  upi_qr: {
    hide_header: 'true',
    hide_footer: 'true',
    hide_progress_bar: 'true',
  },
  bank_transfer: {
    hide_header: 'true',
    hide_footer: 'true',
    hide_progress_bar: 'true',
  },
  test: {
    hide_header: 'true',
    hide_footer: 'true',
    hide_progress_bar: 'true',
  },
  paysense: {
    hide_header: 'true',
    hide_footer: 'true',
    hide_progress_bar: 'true',
  },
  pix_qr: {
    hide_header: 'true',
    hide_footer: 'true',
    hide_progress_bar: 'true',
  },
  spush_pay: {
    hide_header: 'true',
    hide_footer: 'true',
    hide_progress_bar: 'true',
  },
  rapipago: {
    hide_header: 'true',
    hide_footer: 'true',
    hide_progress_bar: 'true',
  },
};

const NavigationFailureType = {
  duplicated: 16,
};
export default {
  mixins: [PaymentStatusController, postMessageService, iframeDetection],
  computed: {
    ...mapState([
      // We need to show loader in cases when we dont use basic render flow, autoprocess for example
      'showGlobalLoader',
      'invoiceInitialData',
      'services',
      'methods',
      'paymentInvoiceStatuses',
      'paymentInvoiceData',
    ]),
    ...mapState('UI', ['layoutConfig']),

    isStepperShown() {
      return !Object.entries(this.layoutConfig).length || !this.layoutConfig.hide_progress_bar;
    },

    isHeaderShown() {
      return !Object.entries(this.layoutConfig).length || !this.layoutConfig.hide_header;
    },

    isFilterShown() {
      return !Object.entries(this.layoutConfig).length || !this.layoutConfig.hide_method_filter;
    },
  },

  async created() {
    try {
      // Init base application state based on passed query params.
      this.SET_APPLICATION_READY_STATE(false);

      const initState = await this.initializeQueryParams();

      // SIMPLE CONTROLLER for handling different flows:
      // 1) Passed payment invoice ID flow;
      if (initState.cpi) {
        this.SET_GLOBAL_LOADER(true);
        await this.handlePaymentInvoiceID(initState.cpi);
        this.SET_GLOBAL_LOADER(false);

        return 1;
      }
      // 2) Basic flow -> render method list and standard UI
      await this.handleBasicFlow(initState);
      // Remove skeletons, show list;
      // this.SET_APPLICATION_READY_STATE(true);
    } catch (e) {
      this.setGlobalError(e);
    } finally {
      this.SET_APPLICATION_READY_STATE(true);
      this.postMessage('rendered');
    }
  },

  methods: {
    ...mapActions([
      'setUITheme',
      'setMethods',
      'setServices',
      'setCategories',
      'setSelectedCategory',
      'setGroupedServices',
      'setGroupedCategories',
      'setCommerceAccountData',
      'setAutoProcessData',
      'selectMethod',
      'setInitState',
      'setTabsNavigationData',
      'getPaymentInvoiceById',
      'setGlobalError',
    ]),

    ...mapMutations([
      'SET_GLOBAL_LOADER',
      'SET_CURRENT_STEP',
      'SET_APPLICATION_READY_STATE',
      'SET_INVOICE_FLOW_DATA_SHOWN',
    ]),
    //
    // Initilize basic state from query parameters.
    // Full list of props you can find here
    // TODO: write documentation
    //
    //
    async initializeQueryParams() {
      let {
        amount,
        currency,
        public_key,
        description = null,
        expires = null,
        reference_id = null,

        theme,
        locale,
        service = null,
        service_fields = null,
        cpi = null,

        display = null,
        style = null,
        metadata = null,

        customer = null,
      } = this.getQueryParameters();

      return await this.setInitState({
        currency,
        theme,
        public_key,
        amount,
        description: description && description.length ? description : null,
        locale,
        service,
        service_fields,
        cpi,
        expires: expires ? expires : null,
        metadata: metadata ? metadata : null,
        display,
        style,
        reference_id,
        customer,
      });
    },

    async intializeMerchantSDK(public_key = '', BASE_URL) {
      try {
        this.PaycoreClient = new MerchantSDK({
          apiKey: public_key,
          BASE_URL,
        });
        return this.PaycoreClient;
      } catch (e) {
        console.log('ERROR', e);
        return e;
      }
    },

    async handleBasicFlow(initState) {
      // Just an a API abstraction
      try {
        await this.intializeMerchantSDK(
          initState.public_key,
          process.env.VUE_APP_PUBLIC_API_URL ||
            `${window.location.protocol}//${window.location.hostname
              .split('.')
              .map((c, i) => (i === 0 && c === 'hpp' ? 'api' : c))
              .join('.')}/public-api`
        );

        const preRequestDataResponse = await this.makePaymentPrerequest(initState.currency);

        if (preRequestDataResponse) {
          this.setPrerequestData(preRequestDataResponse);
        }
        Promise.resolve(true);
      } catch (e) {
        if (e.type && e.type === 'API_CLIENT') {
          console.error('Prerequest network error', e.type, e.message);
          Promise.reject(new Error(e));
        } else {
          throw e;
        }
        // this.setGlobalError(e);
      }
    },

    //  Move it to the mixin
    async handlePaymentInvoiceID(id) {
      try {
        await this.getPaymentInvoiceById(id);
        const { data = {} } = this.paymentInvoiceData;

        const { amount, currency, description } = data;
        this.setInitState({ currency, amount, description });
        const redirectPayload = (data.active_payment || {}).payload || false;
        const paymentInvoiceStatus = data.status;
        const invoiceServiceFlow = data.service_flow;
        const invoiceMethod = data.service_method;

        if (this.paymentInvoiceStatuses[paymentInvoiceStatus] && redirectPayload) {
          const currentStatus = this.paymentInvoiceStatuses[paymentInvoiceStatus];

          if (currentStatus.isFinal) {
            this.$router.push({
              name: 'PaymentStatus',
              params: { id: id },
            });
          } else {
            /**
             * NOT FINAL STATUS, PROCESS NEEDED
             */
            // Flow data in state;
            if (redirectPayload) {
              if (invoiceServiceFlow === 'invoice') {
                this.SET_INVOICE_FLOW_DATA_SHOWN(true);
                this.SET_CURRENT_STEP(2);

                const layoutSettings = layoutSettingsByMethodMap[invoiceMethod] || null;
                if (layoutSettings) {
                  this.$store.dispatch('UI/setLayoutSettings', {
                    ...layoutSettings,
                    ...this.layoutConfig,
                  });
                }

                this.goToInvoiceFlowScreen(data.service_method);
              } else {
                Protocol.redirect(redirectPayload);
              }
            } else {
              throw {
                status: 'no_payload',
              };
            }
          }
        } else {
          // continue base flow;
          throw {
            status: 'no_payload',
          };
        }
      } catch (e) {
        return Promise.reject(e);
      }
    },

    goToInvoiceFlowScreen(pm) {
      this.$router.push({
        name: 'pm',
        params: { pm },
      });
    },

    handleRenderFlow() {
      console.log('HANDLE RENDER FLOW', this.$router);
      this.$router
        .push({
          name: 'PaymentMethodsListPage',
          query: { ...this.$route.query },
        })
        .catch(err => {
          if (err.type !== NavigationFailureType.duplicated) throw err;
        });
    },

    async makePaymentPrerequest(currency) {
      if (this.PaycoreClient) {
        try {
          const preRequestData = await this.PaycoreClient.makePaymentPrerequest(currency || 'USD');
          return preRequestData;
        } catch (e) {
          console.log('OK', e);
          if (!e.error.response) {
            return Promise.reject({
              type: 'API_CLIENT',
              status: 'api_client_network_error',
              message: e.message,
              code: '503',
            });
          }

          if (e.error.response) {
            if (e.error.response.data && e.error.response.data.errors) {
              const { currency = null } = e.error.response.data.errors;
              if (currency) {
                return Promise.reject({
                  status: 'unsupported_currency',
                  message: e.message,
                  code: '400',
                });
              }
            }
          }
          return Promise.reject(e.error.response);
        }
      } else {
        return Promise.reject({ status: 'api_client_network_error' });
      }
    },

    // Destruct and set data to the application state
    async setPrerequestData(preRequestData) {
      let categories = {};

      /**
       *
       * @type {{code: string}[]}
       */
      let methodsArray = Object.keys(preRequestData.data.methods).map(method => {
        return { ...preRequestData.data.methods[method], code: method };
      });
      categories = {
        all: methodsArray,
        ..._.groupBy(methodsArray, 'category'),
      };

      const categoriesSet = this.processCategories(categories);

      const navigationData = categoriesSet.map(el => {
        let newEl = {};
        newEl.callback = 'category';
        newEl.name = el;
        return newEl;
      });

      // TODO: make more readable
      this.setGroupedCategories(categories);
      // Just categories names
      this.setCategories(this.processCategories(categories));
      this.setTabsNavigationData(navigationData);

      // Methods
      this.setMethods(preRequestData.data.methods);
      this.setServices(preRequestData.data.services);
      this.setCommerceAccountData(preRequestData.data.account);

      if (preRequestData.data.services) {
        let servicesArray = Object.keys(preRequestData.data.services).map(k => {
          return {
            ...preRequestData.data.services[k],
            code: k,
            disabled: false,
          };
        });
        const groupedServices = _.groupBy(servicesArray, 'method');

        this.setGroupedServices(groupedServices);
      }

      if (this.invoiceInitialData.service) {
        this.handleServiceAutoProcess(this.invoiceInitialData.service);
      } else {
        // Bas flow - render methods
        this.handleRenderFlow();
      }
    },

    /**
     * @param categories Object with payment method categories
     */
    processCategories(categories) {
      return [...new Set(Object.keys(categories).map(categoryName => categoryName))];
    },

    /**
     * @param service_id Service id that
     */
    handleServiceExisting(service_code) {
      console.log('Service ID', service_code);
      if (this.services) {
        console.log('Obj entires', Object.entries(this.services));
        for (const [k, v] of Object.entries(this.services)) {
          const apSrv = v.find(service => service.code === service_code);
          if (apSrv) {
            return apSrv;
          }
        }
      }
    },
    /**
     * Returns @promise
     * resolve and redirects to payment page with flow
     */
    handleServiceAutoProcess(service_code) {
      console.log('HANDLE AUTO PROCESS', service_code);
      let service = this.handleServiceExisting(service_code);
      if (service) {
        // TODO: Bottle neck, be carefull, the service method can not be in methods array;  🤔 Will be fixed later;
        const selectedMethod = this.methods[service.method];
        /**
         * Set autoProcess to method
         */
        if (selectedMethod) {
          selectedMethod.autoProcess = true;
          this.selectMethod(selectedMethod);
        }
        const autoProcessPayload = {
          service,
          autoProcess: true,
          service_fields_presetted: this.invoiceInitialData.service_fields,
          sercice_code_presetted: this.invoiceInitialData.service,
        };
        this.setAutoProcessData(autoProcessPayload);

        console.log('SERVICE AUTO PROCESS');
        this.$router.push({
          name: 'pm',
          params: { pm: service.method },
        });
      } else {
        console.warn('No passed service exists');
      }
    },
  },

  components: {
    PaymentHeaderComponent,
    Stepper,
    styledPaymentTabSelectComponent,
    MobileStatusBar,
    styledSpinner,
    styledPaymentWrap,
    MobileRemainingTimeCounter,
  },
};
</script>
